import React, { useEffect, useState } from 'react';
import { Trash2, X, RotateCw, Users, Copy } from 'lucide-react';
import GuestSearch from './GuestSearch';
import { SHAPES, FEATURES, plusOneComing, plusOneLabel } from './seatingUtils';

const inp = 'w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]';

const LabelInput = ({ value, onCommit }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => { const t = v.trim(); if (t && t !== value) onCommit(t); else setV(value); };
  return (
    <input data-testid="table-panel-label" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setV(value); } }}
      className="font-serif text-2xl text-[#2A2A2A] bg-transparent focus:outline-none border-b border-transparent focus:border-[#B8896C] flex-1 min-w-0" />
  );
};

export const TablePanel = ({ table, seated, manual, used, guests, tableLabelFor, onUpdate, onSeat, onUnseat, onAddName, onRemoveName, onDelete, onDuplicate }) => (
  <div data-testid="table-panel" className="space-y-3">
    <div className="flex items-center gap-2">
      <LabelInput key={table.id} value={table.label} onCommit={(label) => onUpdate({ label })} />
      <span data-testid="table-panel-count" className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${used > table.seats ? 'bg-[#FFE4EC] text-[#B4235A]' : 'bg-[#F4E4DA] text-[#7B2C3B]'}`}><Users className="w-3 h-3" /> {used}/{table.seats}</span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">Shape
        <select data-testid="table-panel-shape" value={table.shape || 'round'} onChange={(e) => onUpdate({ shape: e.target.value })} className={`${inp} mt-1 normal-case`}>
          {Object.entries(SHAPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">Seats
        <input data-testid="table-panel-seats" type="number" min="1" max="60" value={table.seats} onChange={(e) => onUpdate({ seats: Math.max(1, Math.min(60, Number(e.target.value) || 1)) })} className={`${inp} mt-1`} />
      </label>
    </div>
    {(table.shape === 'rect' || table.shape === 'sweetheart') && (
      <button data-testid="table-panel-rotate" onClick={() => onUpdate({ rotation: Number(table.rotation) === 90 ? 0 : 90 })} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] inline-flex items-center gap-1 hover:bg-[#F6EAE0]"><RotateCw className="w-3 h-3" /> Turn {Number(table.rotation) === 90 ? 'sideways' : 'upright'}</button>
    )}
    <div>
      <p className="text-[11px] uppercase tracking-wide text-[#6A6A6A] mb-1">Add a guest to this table</p>
      <GuestSearch testId="table-guest-search" guests={guests} onPick={onSeat} onFreeText={onAddName} tableLabelFor={tableLabelFor} placeholder="Start typing, e.g. Becky" />
    </div>
    <ul data-testid="table-panel-guests" className="space-y-1.5 max-h-80 overflow-auto">
      {seated.map((g) => (
        <li key={g.id} data-testid={`table-panel-guest-${g.id}`} className="flex items-start gap-2 px-3 py-2 rounded-xl border border-[#EFE3D8] bg-white">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#2A2A2A]">{g.name}{g.rsvp === 'Yes' ? '' : <span className="text-[10px] text-[#9A8A7E]"> · {g.rsvp || 'Pending'}</span>}</p>
            {plusOneComing(g) && <p className="text-[11px] text-[#6A6A6A]">+ {plusOneLabel(g)}</p>}
          </div>
          <button aria-label={`Remove ${g.name}`} data-testid={`table-panel-unseat-${g.id}`} onClick={() => onUnseat(g)} className="text-[#B8896C] hover:text-red-600 mt-0.5"><X className="w-3.5 h-3.5" /></button>
        </li>
      ))}
      {manual.map((n, i) => (
        <li key={`m${i}`} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[#EFE3D8] text-sm">
          <span className="flex-1">{n} <span className="text-[10px] text-[#9A8A7E]">(name only)</span></span>
          <button aria-label={`Remove ${n}`} onClick={() => onRemoveName(i)} className="text-[#B8896C] hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
        </li>
      ))}
      {!seated.length && !manual.length && <li className="text-sm text-[#9A8A7E]">Nobody here yet. Search above, or drag a guest from the list onto the table.</li>}
    </ul>
    <div className="flex gap-3 pt-1">
      <button data-testid="table-panel-duplicate" onClick={onDuplicate} className="text-xs text-[#7B2C3B] inline-flex items-center gap-1 hover:underline"><Copy className="w-3.5 h-3.5" /> Duplicate</button>
      <button data-testid="table-panel-delete" onClick={onDelete} className="text-xs text-[#B8896C] hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete table</button>
    </div>
  </div>
);

export const FeaturePanel = ({ feature, onChange, onCommit, onDelete }) => (
  <div data-testid="feature-panel" className="space-y-3">
    <p className="section-eyebrow">{FEATURES[feature.type].area ? 'Area' : 'Room feature'}</p>
    <input data-testid="feature-panel-label" value={feature.label} placeholder={FEATURES[feature.type].label} onChange={(e) => onChange({ ...feature, label: e.target.value })} onBlur={onCommit} className={inp} />
    <p className="text-xs text-[#6A6A6A]">Drag it into place. Pull the dot in the bottom-right corner to resize.</p>
    <button data-testid="feature-panel-delete" onClick={onDelete} className="text-xs text-[#B8896C] hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
  </div>
);

export default TablePanel;
